import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { updateProfile } from '../../store/reducers/userSlice';
import { setEmployeeIdentity } from '../../store/reducers/authSlice';
import { setMessage } from '../../store/reducers/appSlice';
import { createShop } from '../../lib/api/shop/create_shop';
import { fetchAppInit } from '../../lib/api/app-init';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import AIURLInput from '../../ui/inputs/AIURLInput';
import { submitLogToBugsnag } from '../../lib/api/log';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import extractDomainName from '../../lib/helper-functions/extractDomainName';
import { SHOP_API } from '../../lib/constants/central-api';
import { asyncHandler } from '../../lib/api/async-handler';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { RequestInfo } from '../../interfaces/RequestInfo';

interface AddClientFormProps {
  onClose: () => void;
  onBrandLimitReached?: () => void;
}

const AddClientForm: React.FC<AddClientFormProps> = ({
  onClose,
  onBrandLimitReached,
}) => {
  const dispatch = useDispatch();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  // Set when the name derived from the URL is already one of the
  // organisation's brands: the form then asks for a different name.
  const [takenName, setTakenName] = useState<string | null>(null);
  const [customName, setCustomName] = useState('');

  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );
  const isWhiteLabel = useSelector((state: RootState) => state.UI.isWhiteLabel);

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url || !isNotEmptyString(url)) return;
    if (takenName !== null && !isNotEmptyString(customName.trim())) return;

    setLoading(true);

    try {
      // Derive a distinct brand name from the URL's domain (e.g.
      // "adup", "glampings") — same logic the sidebar AddNewShop uses.
      // Previously this was hardcoded to `${seller_email}'s shop`, so every
      // brand created from this form got the identical name.
      const tempName =
        takenName !== null
          ? customName.trim()
          : extractDomainName(url) || url;
      const webshopUrl = url.startsWith('http') ? url : `https://${url}`;
      // Invite-only employee sessions have no seller JWT: createShop routes
      // to the employee endpoint (which persists the URL itself) and the
      // seller-JWT settings PUT below must be skipped.
      const isEmployeeSession =
        !isWhiteLabel && !isNotEmptyString(accessToken?.sellerToken);
      const shopResponse = await createShop(tempName, webshopUrl);

      if (shopResponse.isLimitReached) {
        setLoading(false);
        onBrandLimitReached?.();
        return;
      }

      if (!shopResponse.success || !shopResponse.data) {
        throw new Error(shopResponse.message || 'Shop creation failed');
      }

      if (shopResponse.alreadyExists) {
        // Nothing was created: stay on the form and ask for another name.
        setTakenName(tempName);
        setCustomName('');
        return;
      }

      const shopSlug = shopResponse.data.slug;

      if (!isEmployeeSession) {
        // Save webshop URL
        const requestInfo: RequestInfo = {
          url: SHOP_API + `/${shopSlug}/setting/shop-general`,
          method: 'PUT',
          body: applicationWWWXRLEncodedBodyBuilder({
            webshop_url: webshopUrl,
          }),
          headers: {
            Accept: 'application/json',
            'Content-type': 'application/x-www-form-urlencoded',
            Authorization: `${accessToken.type} ${
              isWhiteLabel
                ? accessToken.currentWhiteLabelSellerToken
                : accessToken.sellerToken
            }`,
          },
        };

        await asyncHandler(
          requestInfo,
          'SAVE_WEBSHOP_DATA',
          'Website saved.',
          false,
          false,
          true
        );
      }

      dispatch(updateProfile({ active_shop_slug: shopSlug }));
      if (isEmployeeSession) {
        // Re-fetch /me so the new brand lands in accessible_shops — the
        // App.tsx identity bootstrap refills it, and the hydration effect
        // mirrors it into all_shops. fetchAppInit is a seller-JWT call and
        // no-ops for these sessions.
        dispatch(setEmployeeIdentity(null));
      } else {
        fetchAppInit(true);
      }
      onClose();
    } catch (error: any) {
      console.error('Error creating client:', error);
      submitLogToBugsnag('error', `Error creating client: ${error}`);
      dispatch(
        setMessage({
          message: error.message || 'Failed to create client',
          messageType: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submitForm} className='addClientForm'>
      <h3 className='addClientForm__title'>Add new brand</h3>
      <div className='addClientForm__input'>
        <AIURLInput
          id='addClientForm__url-input'
          value={url}
          prefix='https://'
          changeListeners={[
            (e: React.ChangeEvent<HTMLInputElement>) => {
              setUrl(e.target.value);
              setTakenName(null);
              setCustomName('');
            },
          ]}
          placeholder='Enter website URL (e.g. example.com)'
          label=''
        />
      </div>
      {takenName !== null && (
        <div className='addClientForm__input'>
          <p className='addClientForm__error' role='alert'>
            A brand called "{takenName}" already exists. Enter a different
            name for this brand.
          </p>
          <input
            id='addClientForm__name-input'
            type='text'
            className='addClientForm__field'
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder='Brand name'
            maxLength={255}
            autoFocus
            disabled={loading}
          />
        </div>
      )}
      <div className='addClientForm__actions'>
        <button
          type='button'
          className='addClientForm__cancel'
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
        <MainSquareButton
          type='submit'
          value='Add Client'
          normal={true}
          loading={loading}
          disabled={
            !url ||
            !isNotEmptyString(url) ||
            (takenName !== null && !isNotEmptyString(customName.trim()))
          }
        />
      </div>
    </form>
  );
};

export default AddClientForm;
